import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, Modal, Select, message, List, Avatar, Flex, Tag, Badge, Spin, Collapse, Radio, Timeline, Popover, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import { getDocs, collection, query, where, doc, getDoc, setDoc } from "firebase/firestore";
import { fireDB } from "../../funcs/firebase";
import CollabTeamWork from '../homev2/undraw_teamwork_zplp.svg';
import {
    InstagramOutlined,
    FacebookOutlined,
    CalendarOutlined,
    LinkOutlined,
    WhatsAppOutlined,
    PlayCircleFilled,
    CheckCircleFilled,
    GiftOutlined,
    CrownOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    BarChartOutlined,
    AppleFilled,
    AndroidFilled,
    CopyOutlined,
    VideoCameraOutlined,
    RobotOutlined,
    FilePdfOutlined,
    ReloadOutlined,
    DoubleLeftOutlined,
    DoubleRightOutlined
} from '@ant-design/icons';
import Sider from '../../comps/sider/main';
import callAPI from '../../comps/fetch/main';
import ShowPlans from '../../comps/show_plans/main';
import WhatsAppBanner from '../../comps/whatsappBanner/main';
import WhatsNew from '../../comps/whatsNew/main';
import PlanRenewalOffer from '../../comps/PlanRenewalOffer/main';
import createPaymentLink from '../../comps/createCheckout/main';

import './main.css';
import getActiveUserId from '../../funcs/getActiveUserId';
import AiFlowPromptBox from '../../comps/aiFlowPrompt/main';
import IgAutomationMenu from './IgAutomationMenu';
import InsightsSurvey from '../../comps/InsightsSurvey/main';

const { Title, Text } = Typography;

// Instagram-card hover mega-menu — built, parked. Flip to true to enable.
const IG_HOVER_MENU_ENABLED = false;

// Recent product updates shown on the home page — newest first. `id` must be
// a sortable ISO date: dismissal stores the newest id, and the card only
// reappears when a note with a NEWER id ships.
const RELEASE_NOTES = [
    { id: '2026-09-16-leadgen-library', date: 'Sep 16', tag: 'New Feature', text: 'Lead Gen Library — save a published Lead Gen automation and reuse it on any post with Browse Library' },
    { id: '2026-09-16-fb-contacts', date: 'Sep 16', tag: 'New Feature', text: 'Contacts tab now shows Facebook contacts too — switch between Instagram and Facebook' },
    { id: '2026-09-16-human-takeover', date: 'Sep 16', tag: 'AI Flow', text: 'Human takeover switch on each AI reply node — choose whether the AI steps back when you reply yourself' },
    { id: '2026-09-09-rerun-pending', date: 'Sep 9', tag: 'Fix', text: 'Re-run missed comments now also picks up comments whose DM was left pending, not only the failed ones' },
    { id: '2026-09-08-fb-flow', date: 'Sep 8', tag: 'New Feature', text: 'Facebook Flow Automation — build Messenger flows for your Page in the same Flow Editor, with Quick DM for Facebook' },
    { id: '2026-09-08-fb-dm', date: 'Sep 8', tag: 'New Feature', text: 'Facebook DM Automation — reply to Messenger messages automatically' },
    { id: '2026-09-05-variables', date: 'Sep 5', tag: 'New Feature', text: 'Flow Editor: Custom Variables in button and URLs' },
    { id: '2026-09-05', date: 'Sep 5', tag: 'New Feature', text: 'Facebook Webhooks, API and MCP' },
    { id: '2026-08-30', date: 'Aug 30', tag: 'New Feature', text: 'Flow Automation 2.0 — all-new flow editor with AI builder, templates, new logic nodes & live preview' },
    { id: '2026-08-30-contacts', date: 'Aug 29', tag: 'New Feature', text: 'Contacts 1.0 tab — view, tag and manage everyone your automations talk to' },
    { id: '2026-08-14', date: 'Aug 14', tag: 'New Feature', text: 'Automation Recovery — Re-send failed automations' },
    { id: '2026-08-13', date: 'Aug 13', tag: 'For Agencies', text: 'Share Connection Link — Share Instagram and FB connection links with your clients instead of asking for passwords and OTPs' },
    { id: '2026-08-08', date: 'Aug 8', tag: 'For AI users', text: 'MCP for AI agents — Tell Claude / AI agents to reply to comments and DMs' },
    { id: '2026-08-04', date: 'Aug 4', tag: 'AI Flow', text: 'Understands images and audio notes, based on model capabilities' },
    { id: '2026-08-02', date: 'Aug 2', tag: 'New Models', text: 'Gemini / DeepSeek support added' },
];
const LATEST_RELEASE_NOTE_ID = RELEASE_NOTES[0]?.id;
// The card shows only the newest few; the rest sit behind "Load more" so the
// timeline stops growing the page every time a note ships.
const RELEASE_NOTES_PREVIEW = 7;
const RELEASE_NOTES_LS_KEY = 'idm_dismissed_release_note';

function WelcomeOnboardingPage() {
    const navigate = useNavigate();

    const [userName, setUserName] = useState('');




    // Greetings in different languages
    const greetings = [
        'Vanakkam',      // Tamil
        'Namaste',       // Hindi
        'Hello',         // English
        'Hola',          // Spanish
        'Bonjour',       // French
        'Ciao',          // Italian
        'Hallo',         // German
        'Olá',           // Portuguese
        'Konnichiwa',    // Japanese
        'Annyeong',      // Korean
        'Merhaba',       // Turkish
        'Halo',          // Indonesian
    ];
    const [greeting] = useState(() => greetings[Math.floor(Math.random() * greetings.length)]);

    // Trial state
    const [userPlan, setUserPlan] = useState(null);
    const [userPlanLoading, setUserPlanLoading] = useState(true);
    const [trialDaysLeft, setTrialDaysLeft] = useState(7);
    const [isOnTrial, setIsOnTrial] = useState(true);
    const [isFreePlan, setIsFreePlan] = useState(true);
    const [daysSinceCreation, setDaysSinceCreation] = useState(0);

    // Plan modal state
    const [showPlanModal, setShowPlanModal] = useState(false);

    // Yearly offer timer (account created to account created + 24 hours)
    const [yearlyOfferTime, setYearlyOfferTime] = useState(0);
    const [yearlyOfferEndDate, setYearlyOfferEndDate] = useState('');

    useEffect(() => {
        if (!userPlan?.data?.account_created) return;
        try {
            const created = new Date(userPlan.data.account_created).getTime();
            const offerEnd = created + 24 * 60 * 60 * 1000; // 24h after creation
            const now = Date.now();
            if (now > offerEnd) {
                setYearlyOfferTime(0);
                return;
            }
            const remaining = Math.max(0, Math.floor((offerEnd - now) / 1000));
            setYearlyOfferTime(remaining);
            const endDate = new Date(offerEnd);
            setYearlyOfferEndDate(endDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' at ' + endDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }));
        } catch(e) { setYearlyOfferTime(0); }
    }, [userPlan]);

    useEffect(() => {
        if (yearlyOfferTime <= 0) return;
        const id = setInterval(() => setYearlyOfferTime(t => Math.max(0, t - 1)), 1000);
        return () => clearInterval(id);
    }, [yearlyOfferTime]);

    // Collab Invite States
    const [pendingInvites, setPendingInvites] = useState([]);
    const [showCollabModal, setShowCollabModal] = useState(false);

    // Shared Template Prompt State
    const [pendingSharedTemplate, setPendingSharedTemplate] = useState(null);

    // Features display state

    // DM Insights state
    const [dmInsights, setDmInsights] = useState(null);
    const [dmInsightsLoading, setDmInsightsLoading] = useState(false);
    const [dmFilterTag, setDmFilterTag] = useState(null);
    const [dmSortBy, setDmSortBy] = useState('priority');
    // Right sidebar collapse — persisted so the choice sticks across visits.
    const [rightBarCollapsed, setRightBarCollapsed] = useState(() => localStorage.getItem('idm-welcome-rightbar') === 'collapsed');
    const toggleRightBar = () => setRightBarCollapsed((v) => {
        localStorage.setItem('idm-welcome-rightbar', !v ? 'collapsed' : 'open');
        return !v;
    });

    // Account health state
    const [accountHealth, setAccountHealth] = useState(null);
    const [accountHealthLoading, setAccountHealthLoading] = useState(true);
    const [disconnectingFb, setDisconnectingFb] = useState(false);

    useEffect(() => {
        // Check if user is authenticated
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        // Get user name from localStorage
        try {
            const userData = JSON.parse(localStorage.getItem('userData'));
            if (userData?.name) {
                setUserName(userData.name.split(' ')[0]); // Get first name
            } else if (userData?.username) {
                setUserName(userData.username);
            }
        } catch (e) {
            console.log('Error parsing user data');
        }

        // Fetch user plan data
        callAPI({ url: 'user_plan', method: 'GET', loadingState: setUserPlanLoading, dataState: setUserPlan });

        // Fetch account health (IG/FB mismatch check)
        callAPI({ url: 'account-health', method: 'GET', loadingState: setAccountHealthLoading, dataState: setAccountHealth });

        // Fetch DM Insights (sidebar is always visible on desktop now)
        callAPI({ url: 'dm-insights', method: 'GET', loadingState: setDmInsightsLoading, dataState: (res) => {
            if (res?.code === 200) {
                setDmInsights(res);
            }
        }});
    }, [navigate]);

    // Calculate trial days when user plan data is loaded
    useEffect(() => {
        if (!userPlan?.data) return;

        const planData = userPlan.data;
        const currentPlanId = planData.plan_details?.id;

        // Check if user is on free plan
        setIsFreePlan(currentPlanId === 'free');

        // Calculate days since account creation with error handling
        let calculatedDaysSinceCreation = 0; // Default to 0 (new account) on error — benefit of the doubt

        try {
            if (planData.account_created) {
                // Handle Python datetime format "2026-05-22 14:30:45.123456" by replacing space with T
                const dateStr = planData.account_created.replace(' ', 'T');
                const accountCreated = new Date(dateStr);

                // Check if date is valid
                if (!isNaN(accountCreated.getTime())) {
                    const now = new Date();
                    const differenceInTime = now.getTime() - accountCreated.getTime();
                    calculatedDaysSinceCreation = Math.floor(differenceInTime / (1000 * 3600 * 24));

                    // Sanity check: if negative or unreasonably large, default to 0 (treat as new)
                    if (calculatedDaysSinceCreation < 0 || calculatedDaysSinceCreation > 3650) {
                        calculatedDaysSinceCreation = 0;
                    }
                }
            }
        } catch (error) {
            console.error('Error calculating days since creation:', error);
            calculatedDaysSinceCreation = 0; // Default to new account on error (benefit of the doubt)
        }

        setDaysSinceCreation(calculatedDaysSinceCreation);

        // Check if still in trial period (less than 8 days old)
        if (calculatedDaysSinceCreation < 8 && currentPlanId === 'free') {
            setIsOnTrial(true);
            setTrialDaysLeft(7 - calculatedDaysSinceCreation);

            // Set the temporary plan ID for trial users
            localStorage.setItem('og_planID', currentPlanId);
            localStorage.setItem('planid', '428337'); // Pro plan ID for trial
        } else {
            setIsOnTrial(false);
            setTrialDaysLeft(0);
        }
    }, [userPlan]);

    // Fetch Pending Collab Invites
    useEffect(() => {
        const fetchPendingInvites = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;
            let username = "";
            try {
                const decoded = jwtDecode(token);
                username = decoded.default_page?.username || decoded.username;
            } catch (e) {
                console.error("Token decode error:", e);
                return;
            }

            if (!username) return;

            try {
                const q = query(
                    collection(fireDB, "collab_invitations"),
                    where("owner_username", "==", username),
                    where("status", "==", "pending")
                );
                const querySnapshot = await getDocs(q);
                const invites = [];
                querySnapshot.forEach((doc) => {
                    invites.push({ id: doc.id, ...doc.data() });
                });
                setPendingInvites(invites);
            } catch (err) {
                console.error("Error fetching invites:", err);
            }
        };
        fetchPendingInvites();

        // Check for pending shared template
        const checkPendingSharedTemplate = async () => {
            const pendingId = localStorage.getItem("pending_shared_template_id");
            if (pendingId) {
                try {
                    const docRef = doc(fireDB, "shared_templates", pendingId);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        setPendingSharedTemplate({ id: docSnap.id, ...docSnap.data() });
                    } else {
                        // Invalid ID, clear it
                        localStorage.removeItem("pending_shared_template_id");
                        localStorage.removeItem("show_pending_shared_template");
                    }
                } catch (error) {
                    console.error("Error fetching pending shared template:", error);
                }
            }
        };
        checkPendingSharedTemplate();
    }, []);

    // Auto-show modal when invites are fetched
    useEffect(() => {
        if (pendingInvites.length > 0 && !showCollabModal) {
            setShowCollabModal(true);
        }
    }, [pendingInvites]);

    const tutorials = [
        {
            id: 1,
            title: 'Automate Instagram Comments',
            description: 'Learn how to set up comment to DM automation for your posts',
            gradient: 'linear-gradient(135deg, #E1306C 0%, #833AB4 100%)',
            videoId: '5cdU1e1NAT0',
            duration: '5:00'
        },
        {
            id: 2,
            title: 'Global Automation',
            description: 'Automate all your posts and reels at once',
            gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            videoId: 'Fi4NKgzC19w',
            duration: '2:00'
        },
        {
            id: 3,
            title: 'Facebook Automation',
            description: 'Set up Facebook comment to Messenger automation',
            gradient: 'linear-gradient(135deg, #1877F2 0%, #0D47A1 100%)',
            videoId: '4HE7B23NClY',
            duration: '2:10'
        },
        {
            id: 4,
            title: 'Schedule Instagram Posts with AutoDM',
            description: 'Plan and schedule your content with automation',
            gradient: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
            videoId: '_28lJKsayLY',
            duration: '6:00'
        },
    ];

    const features = [
        {
            key: 'instagram',
            icon: <InstagramOutlined />,
            title: 'Instagram Automation',
            description: 'Comment to DM automation for Instagram posts & reels',
            path: '/ig'
        },
        {
            key: 'facebook',
            icon: <FacebookOutlined />,
            title: 'Facebook Automation',
            description: 'Automate Facebook page comments to Messenger',
            path: '/fb'
        },
        {
            key: 'later',
            icon: <CalendarOutlined />,
            title: 'Schedule Posts with AutoDM',
            description: 'Plan and schedule your content with automation',
            path: '/later',
        },
        {
            key: 'dashboard',
            icon: <BarChartOutlined />,
            title: 'Dashboard',
            description: 'View your automation stats and analytics',
            path: '/insights'
        },
        {
            key: 'oia',
            icon: <LinkOutlined />,
            title: 'Open In App',
            description: 'Smart links that open directly in apps',
            path: '/oia'
        },
        {
            key: 'referral',
            icon: <DollarOutlined />,
            title: 'Referral Dashboard',
            description: 'Earn up to 50% commission for every referral',
            path: '/referral-dashboard'
        }
    ];

    // Release-notes dismissal marker: undefined = still loading (card hidden),
    // null = never dismissed, else the newest note id at dismissal time.
    const [releaseNotesExpanded, setReleaseNotesExpanded] = useState(false);
    const [releaseNotesDismissedUpTo, setReleaseNotesDismissedUpTo] = useState(
        localStorage.getItem(RELEASE_NOTES_LS_KEY) || undefined
    );

    const [paymentLinkData, setPaymentLinkData] = useState()
    const [paymentLinkLoading, setPaymentLinkLoading] = useState()
    const [showINR, setShowINR] = useState(false)
    const [showWaitingPaymentUpdate, setShowWaitingPaymentUpdate] = useState(false)
    const [showWaitingPaymentFailed, setShowWaitingPaymentFailed] = useState(false)
    const [messageApi, contextHolder] = message.useMessage();

    // Determine currency based on timezone
    useEffect(() => {
        const user_tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setShowINR(user_tz === "Asia/Kolkata" || user_tz === "Asia/Calcutta");
    }, []);

    // Payment processing logic
    useEffect(() => {
        if (paymentLinkData?.open_url) {
            window.location.href = paymentLinkData?.payment_url
            return
        }

        if (paymentLinkData?.payment_url == undefined) {
            if (paymentLinkData?.code || paymentLinkData?.message) {
                messageApi.destroy("payment-process")
                message.error(paymentLinkData?.message || "Please logout and login to continue.")
                try {
                    let user_detail = JSON.parse(localStorage.getItem("userData"));
                    let igid = localStorage.getItem("ig_id");
                    const BOT_TOKEN = "7109212238:AAEsaFubA66rs0Ib6rgn5K2amKO9rnOkUv0";
                    const CHAT_ID = '776956029';
                    fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ chat_id: CHAT_ID, text: `[SUBSCRIBE FAILED - Please wait keep loading] - ${user_detail?.emailid} - IG ID = ${igid}, user id = ${getActiveUserId(true)}, error: ${paymentLinkData?.message || 'unknown'}, source = ${sessionStorage.getItem('payment_source') || 'unknown'}` })
                    }).catch(() => {});
                } catch(e) {}
                setPaymentLinkData({})
            }
            return
        }

        messageApi.destroy("payment-process")

        if (!showINR && window.LemonSqueezy) {
            const checkoutUrl = paymentLinkData.payment_url
            window.LemonSqueezy.Url.Open(checkoutUrl);
        }

        if (showINR && window.Razorpay) {
            try {
                const options = {
                    key: "rzp_live_JtjGCHjkESNzIZ",
                    name: "InstantDM",
                    description: "Affordable Comment to DM automation tool",
                    subscription_id: paymentLinkData.rzpy_sub_id,
                    handler: function (response) {
                        setShowPlanModal(false)
                        setShowWaitingPaymentUpdate(true)

                        message.success("Payment completed, Please wait while we're verfiying!")
                        let user_detail = JSON.parse(localStorage.getItem("userData"))
                        let igid = localStorage.getItem("ig_id")

                        const BOT_TOKEN = "7109212238:AAEsaFubA66rs0Ib6rgn5K2amKO9rnOkUv0";
                        const CHAT_ID = '776956029';
                        const url = `https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`;
                        const planName = sessionStorage.getItem('payment_plan_name') || 'unknown';
                        const billing = sessionStorage.getItem('payment_billing') || 'unknown';
                        const currency = sessionStorage.getItem('payment_currency') || 'inr';
                        const gateway = sessionStorage.getItem('payment_gateway') || 'razorpay';
                        let tele_message = `[SUBSCRIBED PLAN] - user ${user_detail?.emailid} - Plan: ${planName} ${billing}, Gateway: ${gateway}, Currency: ${currency.toUpperCase()}, IG ID = ${igid}, user id = ${getActiveUserId(true)}, source = ${sessionStorage.getItem('payment_source') || 'unknown'}`
                        let data = {
                            chat_id: CHAT_ID,
                            text: tele_message
                        };

                        fetch(url, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(data)
                        })
                            .then(response => response.json())
                            .then(data => {
                                console.log('Message sent successfully:', data);
                            })
                            .catch(error => {
                                console.error('Error sending message:', error);
                            });
                        setPaymentLinkData({})
                    },
                    error: function (response) {
                        setShowPlanModal(false)
                        setShowWaitingPaymentFailed(true)
                        setPaymentLinkData({})
                    },
                    prefill: {
                        name: paymentLinkData.customer_name,
                        email: paymentLinkData.customer_email,
                    },
                    "notes": paymentLinkData.notes != undefined ? { ...paymentLinkData.notes } : {},
                    theme: {
                        color: "#1677ff",
                    },
                    display: {
                        handler: function () {
                            return {
                                "offers": [
                                    {
                                        "type": "discount",
                                        "description": "10% discount on first purchase",
                                        "amount": 100
                                    },
                                    {
                                        "type": "coupon",
                                        "description": "Rs. 100 off on total bill",
                                        "code": "INSTANT10"
                                    }
                                ]
                            }
                        }
                    },
                };

                const razorpayInstance = new window.Razorpay(options);
                razorpayInstance.open();

            } catch (error) {
                alert("Error loading Razorpay: " + error.message);
            }
        } else if (!showINR && !window.LemonSqueezy) {
            // Fallback if LemonSqueezy is not loaded although it should be
            // message.error("Payment provider not loaded. Please refresh.");
        }
    }, [paymentLinkData, showINR, messageApi]);

    const handleFeatureClick = (feature) => {
        navigate(feature.path);
    };

    const openWhatsApp = () => {
        window.open('https://wa.me/919876543210?text=Hi, I need help setting up InstantDM', '_blank');
    };

    // Load the release-notes dismissal marker from Firestore (localStorage is
    // the fast mirror; Firestore is the source of truth across devices)
    useEffect(() => {
        const uid = getActiveUserId(true);
        if (!uid) { setReleaseNotesDismissedUpTo((prev) => prev === undefined ? null : prev); return; }
        getDoc(doc(fireDB, "user_prefs", String(uid)))
            .then((snap) => {
                const dismissed = snap.exists() ? (snap.data()?.dismissed_release_note || null) : null;
                setReleaseNotesDismissedUpTo(dismissed);
                if (dismissed) localStorage.setItem(RELEASE_NOTES_LS_KEY, dismissed);
                else localStorage.removeItem(RELEASE_NOTES_LS_KEY);
            })
            .catch(() => setReleaseNotesDismissedUpTo((prev) => prev === undefined ? null : prev));
    }, []);

    const dismissReleaseNotes = () => {
        setReleaseNotesDismissedUpTo(LATEST_RELEASE_NOTE_ID);
        localStorage.setItem(RELEASE_NOTES_LS_KEY, LATEST_RELEASE_NOTE_ID);
        const uid = getActiveUserId(true);
        if (!uid) return;
        setDoc(doc(fireDB, "user_prefs", String(uid)), { dismissed_release_note: LATEST_RELEASE_NOTE_ID }, { merge: true }).catch(() => { });
    };

    // Visible while loading only if localStorage already says dismissed=older;
    // hidden when the stored marker is >= the newest note id.
    const showReleaseNotes = LATEST_RELEASE_NOTE_ID &&
        releaseNotesDismissedUpTo !== undefined &&
        (!releaseNotesDismissedUpTo || LATEST_RELEASE_NOTE_ID > releaseNotesDismissedUpTo);

    // Remaining credits for the trial banner (plan + topup, same math as
    // homev2) — the trial ends at whichever runs out first, days or credits
    const trialCreditsLeft = (() => {
        const d = userPlan?.data;
        if (!d || d.plan_credits === "unlimited") return null;
        const n = Number(d.plan_credits) + Number(d.topup_credit || 0);
        return Number.isFinite(n) ? Math.max(0, n) : null;
    })();

    // Compact "More features" + "Tutorials" lists — shown in the right sidebar
    // on desktop and inside a collapse on mobile. Same click targets as the
    // old full-size sections.
    const renderCompactExtras = () => (
        <div>
            <div style={{ marginBottom: 14 }}>
                <p className="side-mini-label">More features</p>
                {features.slice(3).map((f) => (
                    <div key={f.key} className="side-mini-row" onClick={() => handleFeatureClick(f)}>
                        <div className="side-mini-icon">{f.icon}</div>
                        <p className="side-mini-title">{f.title}</p>
                    </div>
                ))}
            </div>
            <div style={{ marginBottom: 4 }}>
                <p className="side-mini-label">Tutorials</p>
                {tutorials.map((t) => (
                    <div
                        key={t.id}
                        className="side-mini-row"
                        onClick={() => window.open(`https://www.youtube.com/watch?v=${t.videoId}`, '_blank')}
                    >
                        <img
                            className="side-mini-thumb"
                            src={`https://img.youtube.com/vi/${t.videoId}/default.jpg`}
                            alt={t.title}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <p className="side-mini-video-title">{t.title}</p>
                            <span className="side-mini-meta">{t.duration}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="page-layout">
            {contextHolder}
            <Sider />
            <div className="page-content welcome-home-page">

            <InsightsSurvey accountCreatedAt={userPlan?.data?.account_created} />

            <PlanRenewalOffer
              userPlan={userPlan}
              userPlanLoading={userPlanLoading}
              onUpgrade={() => setShowPlanModal(true)}
            />

            <div className="welcome-header">
                    <div className="welcome-header-content">
                        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
                            <Title level={2} className="welcome-title" style={{ margin: 0 }}>
                                {userName ? `${userName}` : ''}
                            </Title>
                            <WhatsNew setShowPlanModal={setShowPlanModal} inline={true} />
                        </div>
                      {!isFreePlan &&  <Text className="welcome-subtitle">
                            Facebook + Instagram Comment, stories, DM Automation
                        </Text> }
                    </div>
                </div>

                {/* Instagram Comment Reply Disruption Notice */}

                {/* Account Health: IG/FB Page Mismatch Warning */}
                {accountHealth?.data?.mismatch && (
                    <div style={{
                        background: '#fff2f0',
                        border: '1px solid #ffccc7',
                        borderLeft: '4px solid #ff4d4f',
                        borderRadius: '8px',
                        padding: '14px 18px',
                        marginBottom: '16px',
                        maxWidth: '500px',
                        margin: '0 auto 16px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                    }}>
                        <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
                        <div>
                            <h4 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: '#cf1322' }}>Instagram & Facebook Page Mismatch</h4>
                            <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#8c4646', lineHeight: '1.5' }}>
                                Your Instagram account <strong>@{accountHealth.data.ig_username}</strong> doesn't match the Instagram linked to your Facebook Page
                                {accountHealth.data.fb_page_name ? ` "${accountHealth.data.fb_page_name}"` : ""} which is <strong>@{accountHealth.data.fb_connected_ig_username}</strong>.
                                This will cause automations to not work.
                            </p>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <Button type="primary" danger size="small" onClick={() => navigate('/ig-connect')}>Reconnect FB Page</Button>
                                <Button size="small" danger ghost loading={disconnectingFb} onClick={() => {
                                    setDisconnectingFb(true);
                                    callAPI({
                                        url: 'disconnect-fb-page', method: 'POST',
                                        loadingState: setDisconnectingFb,
                                        dataState: (res) => {
                                            if (res?.code === 200 || res?.message?.toLowerCase().includes('success')) {
                                                message.success('Facebook Page disconnected');
                                                setAccountHealth(null);
                                            } else {
                                                message.error(res?.message || 'Failed to disconnect');
                                            }
                                        }
                                    });
                                }}>Disconnect FB Page</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Yearly Offer Card */}
                {!userPlanLoading && isFreePlan && yearlyOfferTime > 0 && (
                    <div style={{ background: 'radial-gradient(ellipse at top left, #1a0000 0%, #0a0a0a 40%, #1a0000 70%, #cc0000 100%)', borderRadius: 14, padding: '28px 32px', marginBottom: 16, maxWidth: 540, margin: '0 auto 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                        <style>{`.offer-cta-btn{transition:all .2s ease;animation:pulse-btn 2s ease-in-out infinite}.offer-cta-btn:hover{transform:scale(1.03);box-shadow:0 6px 24px rgba(255,255,255,.3)!important;animation:none}@keyframes pulse-btn{0%,100%{transform:scale(1)}50%{transform:scale(1.02)}}`}</style>
                        <div style={{ position: 'absolute', top: 0, right: 0, width: 200, height: 200, background: 'radial-gradient(circle, rgba(200,0,0,0.3) 0%, transparent 70%)', borderRadius: '50%' }} />
                        <div style={{ position: 'absolute', inset: 0, opacity: 0.06, backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%270 0 200 200%27 xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.65%27 numOctaves=%273%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/%3E%3C/svg%3E")', backgroundSize: '150px' }} />
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#ff4444', textTransform: 'uppercase', letterSpacing: 1.5 }}>⚡ Sale ends {yearlyOfferEndDate}</p>
                            <h2 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 900, color: '#ffffff', letterSpacing: -0.5 }}>{showINR ? 'Pay 6 Months, Get 1 Full Year' : 'Pay 4 Months, Get 1 Full Year'}</h2>
                            <p style={{ margin: '0 0 20px', fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>Trendsetter Plan — Unlimited DMs, AI Agent, Flow Editor, FB + Instagram</p>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: '10px 20px', marginBottom: 20, border: '1px solid rgba(255,255,255,0.1)' }}>
                                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>Offer ends in</span>
                                <span style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 800, color: '#ff4444' }}>{String(Math.floor(yearlyOfferTime / 3600)).padStart(2, '0')}:{String(Math.floor((yearlyOfferTime % 3600) / 60)).padStart(2, '0')}:{String(yearlyOfferTime % 60).padStart(2, '0')}</span>
                            </div>
                            <div style={{ display: 'block' }}>
                                <Button className="offer-cta-btn" type="primary" size="large" onClick={() => { try { const ud = JSON.parse(localStorage.getItem("userData") || "{}"); fetch(`https://api.telegram.org/bot7109212238:AAEsaFubA66rs0Ib6rgn5K2amKO9rnOkUv0/sendMessage`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ chat_id: '776956029', text: `[YEARLY OFFER] ${ud?.emailid||''} uid:${getActiveUserId(true)} ${showINR?'INR':'USD'}` }) }).catch(()=>{}); } catch(e){} sessionStorage.setItem('payment_source','yearly_offer_card'); createPaymentLink('428338', showINR?'inr':'usd', messageApi, setPaymentLinkLoading, setPaymentLinkData, showINR?'razorpay':'stripe', false, showINR?'offer_Sn7BIgVtS75pNw':'promo_1TFntmSZZ7x3Jx8pHvelouhI'); }} loading={paymentLinkLoading} style={{ borderRadius: 8, fontWeight: 700, height: 46, paddingInline: 32, background: '#fff', borderColor: '#fff', color: '#0a0a0a', fontSize: 15, boxShadow: '0 4px 16px rgba(255,255,255,0.2)' }}>Claim Offer →</Button>
                            </div>
                            <p style={{ margin: '12px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Secure checkout • Cancel anytime</p>
                        </div>
                    </div>
                )}


                {/* Free Trial Banner - Only show for free plan users */}
                {!userPlanLoading && isFreePlan && (
                    <div className={`free-trial-banner ${!isOnTrial ? 'trial-expired' : ''}`}>
                        <div className="trial-icon">
                            {isOnTrial ? <GiftOutlined /> : <ClockCircleOutlined />}
                        </div>
                        <div className="trial-content">
                            <div className="trial-badge">
                                <CrownOutlined /> {isOnTrial ? 'PRO PLAN TRIAL' : 'FREE PLAN'}
                            </div>
                            {isOnTrial ? (
                                <>
                                    <h3>{trialDaysLeft > 0 ? `${trialDaysLeft} days${trialCreditsLeft != null ? ` or ${trialCreditsLeft} credits` : ''} left` : 'Last day'} of your Pro trial</h3>
                                    <p>Enjoying premium features no credit card required. {trialDaysLeft > 0 ? `Reverts to Free plan in ${trialDaysLeft} days${trialCreditsLeft != null ? ' or when credits run out' : ''}.` : 'Reverts to Free plan tomorrow.'}</p>
                                </>
                            ) : (
                                <>
                                    <h3>Your free trial has ended</h3>
                                    <p>Upgrade to Pro for unlimited automations and premium features.</p>
                                </>
                            )}
                        </div>
                        <div className="trial-actions">
                            <Button type="default" className="upgrade-btn" onClick={() => setShowPlanModal(true)}>
                                {!isOnTrial ? 'Upgrade Now' : 'Show Plans'}
                            </Button>
                        </div>
                    </div>
                )}


                {/* Mobile DM Insights */}
                {dmInsights?.data?.length > 0 && (
                    <div className="mobile" style={{ marginBottom: '16px' }}>
                        <Collapse
                            ghost
                            items={[{
                                key: 'dm-insights-mobile',
                                label: (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>✨</span>
                                        <span style={{ fontWeight: 600, fontSize: '14px' }}>DM Insights</span>
                                        {dmInsights?.counts?.unread > 0 && <Badge count={dmInsights.counts.unread} size="small" />}
                                    </div>
                                ),
                                children: (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {dmInsights.data.slice(0, 10).map((item) => {
                                            const priorityColor = item.priority === 'high' ? '#ff4d4f' : item.priority === 'medium' ? '#faad14' : '#52c41a';
                                            const timeAgo = (() => {
                                                const diff = Date.now() - new Date(item.last_seen).getTime();
                                                const mins = Math.floor(diff / 60000);
                                                if (mins < 60) return `${mins}m`;
                                                const hrs = Math.floor(mins / 60);
                                                if (hrs < 24) return `${hrs}h`;
                                                return `${Math.floor(hrs / 24)}d`;
                                            })();
                                            return (
                                                <div
                                                    key={item.sender_id}
                                                    onClick={() => {
                                                        if (!item.read) {
                                                            callAPI({ url: 'dm-insights', method: 'PUT', body: { sender_id: item.sender_id }, loadingState: () => {}, dataState: () => {} });
                                                            setDmInsights(prev => ({
                                                                ...prev,
                                                                data: prev.data.map(d => d.sender_id === item.sender_id ? { ...d, read: true } : d),
                                                                counts: { ...prev.counts, unread: Math.max(0, (prev.counts?.unread || 0) - 1) }
                                                            }));
                                                        }
                                                        if (item.sender_username) window.open(`https://instagram.com/${item.sender_username}`, '_blank');
                                                    }}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px',
                                                        borderRadius: '8px', cursor: 'pointer',
                                                        background: item.read ? 'white' : '#f6f8ff',
                                                        border: `1px solid ${item.read ? '#f0f0f0' : '#e0e8ff'}`,
                                                    }}
                                                >
                                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: priorityColor, flexShrink: 0 }} />
                                                    <Avatar size={32} src={item.sender_detail?.profile_pic}>
                                                        {(item.sender_username || '?')[0]?.toUpperCase()}
                                                    </Avatar>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <span style={{ fontWeight: item.read ? 500 : 700, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {item.sender_username ? `@${item.sender_username}` : item.sender_id?.slice(0, 10)}
                                                            </span>
                                                            <Tag color="blue" style={{ margin: 0, fontSize: '10px', padding: '0 4px' }}>{item.current_intent}</Tag>
                                                        </div>
                                                        <p style={{ margin: 0, fontSize: '11px', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {item.intent_timeline?.[item.intent_timeline.length - 1]?.text || ''}
                                                        </p>
                                                    </div>
                                                    <span style={{ fontSize: '11px', color: '#aaa', flexShrink: 0 }}>{timeAgo}</span>
                                                    <Button size="small" type="text" danger style={{ flexShrink: 0, fontSize: 10, padding: '0 4px', height: 18 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            callAPI({ url: 'dm-insights', method: 'DELETE', body: { sender_id: item.sender_id }, loadingState: () => {}, dataState: () => {} });
                                                            setDmInsights(prev => ({
                                                                ...prev,
                                                                data: prev.data.filter(d => d.sender_id !== item.sender_id),
                                                                counts: { ...prev.counts, total: Math.max(0, (prev.counts?.total || 0) - 1) }
                                                            }));
                                                        }}
                                                    >✕</Button>
                                                </div>
                                            );
                                        })}
                                        <Button type="link" block onClick={() => navigate('/ig?tab=3')}>View All →</Button>
                                    </div>
                                )
                            }]}
                        />
                    </div>
                )}

                {/* Team Access Beta Banner — hidden */}


  {/* Reusable Sections */}
                {(() => {
                    // Features Section Component
                    const FeaturesSection = (
                        <div className="welcome-section" key="features">
                            {pendingInvites.length > 0 && (
                                <div className="collab-request-banner" onClick={() => setShowCollabModal(true)}>
                                    <div className="collab-banner-content">
                                        <img src={CollabTeamWork} alt="Collab" style={{ height: 60 }} />
                                        <div className="collab-banner-text">
                                            <h4>Collaboration requests pending</h4>
                                            <p>Review and automate posts with your Brands and Collaboration.</p>
                                        </div>
                                    </div>
                                    <Button type="primary" className="view-now-btn">
                                        View Now
                                    </Button>
                                </div>
                            )}

                            {/* Describe it → AI builds the flow → editor opens */}
                            <AiFlowPromptBox style={{ marginBottom: 16 }} />

                            <div className="features-grid">
                                {features.slice(0, 3).map((feature) => {
                                    const card = (
                                        <div
                                            className="feature-card"
                                            onClick={() => handleFeatureClick(feature)}
                                        >
                                            <div className="feature-icon">
                                                {feature.icon}
                                            </div>
                                            <div className="feature-content">
                                                <div className="feature-title-row">
                                                    <h4>{feature.title}</h4>
                                                    {feature.badge && (
                                                        <span className="feature-badge">{feature.badge}</span>
                                                    )}
                                                </div>
                                                <p>{feature.description}</p>
                                            </div>
                                        </div>
                                    );
                                    // Instagram card gets the big hover menu:
                                    // quick recipes + flow templates at a glance.
                                    return feature.key === 'instagram' && IG_HOVER_MENU_ENABLED ? (
                                        <Popover
                                            key={feature.key}
                                            placement="bottom"
                                            trigger="hover"
                                            mouseEnterDelay={0.2}
                                            overlayInnerStyle={{ padding: 18, borderRadius: 14 }}
                                            content={<IgAutomationMenu />}
                                        >
                                            {card}
                                        </Popover>
                                    ) : (
                                        <span key={feature.key} style={{ display: 'contents' }}>{card}</span>
                                    );
                                })}
                            </div>
                            
                        </div>
                    );

                    // Recent updates timeline — dismissible; stays hidden
                    // until a newer note than the dismissed marker ships
                    const ReleaseNotesSection = showReleaseNotes ? (
                        <div className="welcome-section" key="release-notes">
                            <div className="release-notes-card">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <p className="side-mini-label" style={{ margin: 0 }}>Recent updates</p>
                                    <Button type="text" size="small" onClick={dismissReleaseNotes} style={{ color: 'var(--idm-text-faint)' }}>
                                        ✕
                                    </Button>
                                </div>
                                <Timeline
                                    style={{ marginTop: 16 }}
                                    items={(releaseNotesExpanded ? RELEASE_NOTES : RELEASE_NOTES.slice(0, RELEASE_NOTES_PREVIEW)).map((note) => ({
                                        color: 'var(--idm-brand-500)',
                                        children: (
                                            <div>
                                                <span className="release-note-date">{note.date}</span>
                                                {note.tag && <span className="release-note-tag">{note.tag}</span>}
                                                <span className="release-note-text">{note.text}</span>
                                            </div>
                                        ),
                                    }))}
                                />
                                {RELEASE_NOTES.length > RELEASE_NOTES_PREVIEW && (
                                    <Button
                                        type="link"
                                        size="small"
                                        style={{ padding: 0, marginTop: -8, color: 'var(--idm-text-faint)' }}
                                        onClick={() => setReleaseNotesExpanded((v) => !v)}
                                    >
                                        {releaseNotesExpanded ? 'Show less' : `Load more (${RELEASE_NOTES.length - RELEASE_NOTES_PREVIEW})`}
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : null;

                    // Mobile-only compact access to the remaining features +
                    // tutorials (the desktop versions live in the right sidebar)
                    const MobileExtrasSection = (
                        <div className="mobile welcome-section" key="mobile-extras">
                            <Collapse
                                ghost
                                items={[{
                                    key: 'extras',
                                    label: <span style={{ fontWeight: 600, fontSize: '14px' }}>More features & tutorials</span>,
                                    children: renderCompactExtras()
                                }]}
                            />
                        </div>
                    );

                    // Mobile App Section
                    const MobileAppSection = (
                        <div className="welcome-home-page" key="mobile-app" style={{ padding: 0 }}>
                            <div className="mobile-app-banner">
                                <div className="mobile-app-content">
                                    <h3>Download InstantDM App</h3>
                                    <p>Manage your automations, reply to DMs, and track analytics on the go. Available for iOS and Android.</p>
                                </div>
                                <div className="mobile-app-actions">
                                    <Button
                                        href="https://apps.apple.com/in/app/insta-dm-app-instantdm/id6756658913"
                                        target="_blank"
                                        icon={<AppleFilled style={{ fontSize: 18 }} />}
                                        className="app-store-btn"
                                    >
                                        App Store
                                    </Button>
                                    <Button
                                        href="https://play.google.com/store/apps/details?id=com.replbetter.instantdm"
                                        target="_blank"
                                        icon={<AndroidFilled style={{ fontSize: 18, color: '#3DDC84' }} />}
                                        className="app-store-btn"
                                        style={{ background: '#222 !important', color: 'white !important', border: '1px solid #333 !important' }}
                                    >
                                        Play Store
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );

                    // Shared Template Banner
                    const SharedTemplateBanner = pendingSharedTemplate ? (
                        <div className="welcome-section" key="shared-template" style={{ marginTop: 24 }}>
                            <div className="collab-request-banner" style={{
                                background: 'var(--idm-surface)',
                                borderLeft: '4px solid var(--idm-brand-500)',
                                cursor: 'default',
                                boxShadow: 'var(--idm-shadow-sm)'
                            }}>
                                <div className="collab-banner-content">
                                    <div style={{
                                        background: 'var(--idm-brand-50)',
                                        padding: 12,
                                        borderRadius: 12, // slightly rounded square looks more modern than circle often
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <GiftOutlined style={{ fontSize: 24, color: 'var(--idm-brand-600)' }} />
                                    </div>
                                    <div className="collab-banner-text">
                                        <h4 style={{ color: '#1a1a2e', fontSize: 16, marginBottom: 4 }}>Pending Shared Automation</h4>
                                        <p style={{ color: '#666', fontSize: 14, margin: 0 }}>
                                            You have a shared automation <strong>"{pendingSharedTemplate.name}"</strong> waiting to be copied.
                                        </p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <Button
                                        type="text"
                                        onClick={() => {
                                            localStorage.removeItem("pending_shared_template_id");
                                            localStorage.removeItem("show_pending_shared_template");
                                            setPendingSharedTemplate(null);
                                        }}
                                        style={{ color: '#888' }}
                                    >
                                        Dismiss
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<CopyOutlined />}
                                        style={{ borderRadius: 8, height: 38, padding: '0 20px' }}
                                        onClick={() => {
                                            // Clear pending state and redirect
                                            localStorage.removeItem("pending_shared_template_id");
                                            localStorage.removeItem("show_pending_shared_template");
                                            navigate(`/flow?shared_id=${pendingSharedTemplate.id}`);
                                        }}
                                    >
                                        Copy Now
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : null;

                    return [SharedTemplateBanner, FeaturesSection, MobileExtrasSection, MobileAppSection, ReleaseNotesSection];
                })()}
                {/* Header Section */}
                



                
              

                {/* <WhatsAppBanner /> */}
            </div>


            {/* Plan Modal */}
            <Modal
                title="Plans"
                open={showPlanModal}
                destroyOnClose
                onCancel={() => setShowPlanModal(false)}
                footer={null}
                width='100%'
            >
                <ShowPlans
                    currentPlan={localStorage.getItem("planid")}
                    messageApi={messageApi}
                    setPaymentLinkLoading={setPaymentLinkLoading}
                    setPaymentLinkData={setPaymentLinkData}
                    source="welcome_page"
                />
            </Modal>

            {/* Collab Invitations Modal */}
            <Modal
                open={showCollabModal && pendingInvites?.length > 0}
                footer={null}
                onCancel={() => setShowCollabModal(false)}
                centered
                title="Pending Collaboration Requests"
            >
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <img src={CollabTeamWork} alt="Collaboration" style={{ height: 120, marginBottom: 16 }} />
                    <p style={{ color: "#666" }}>You have pending requests to automate posts. Review them to start collaborating.</p>
                </div>

                <List
                    itemLayout="horizontal"
                    dataSource={pendingInvites}
                    renderItem={(item) => (
                        <List.Item
                            actions={[<Button type="primary" onClick={() => {
                                window.location.href = `/manage-post/${item.post.id}?invite_id=${item.id}&action=review`;
                            }}>Review</Button>]}
                        >
                            <List.Item.Meta
                                avatar={<Avatar src={item.sender?.picture} />}
                                title={<a href="#">{item.sender?.username}</a>}
                                description={
                                    <div>
                                        <p style={{ margin: 0, fontSize: 12 }}>Requested to automate post</p>
                                        <Flex gap={5} style={{ marginTop: 5 }} align="center">
                                            <img src={item.post?.image} style={{ width: 30, height: 30, borderRadius: 4, objectFit: "cover" }} />
                                            <p style={{ margin: 0, fontSize: 10, color: "#999", maxWidth: 150, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.post?.caption}</p>
                                        </Flex>
                                    </div>
                                }
                            />
                        </List.Item>
                    )}

                />
            </Modal>

            {/* Right Sidebar — DM Insights (Desktop only), collapsible */}
            {window.innerWidth > 1000 && rightBarCollapsed ? (
                <div
                    className="desktop"
                    style={{
                        width: '40px',
                        minWidth: '40px',
                        height: '100vh',
                        borderLeft: '1px solid var(--idm-border)',
                        background: 'var(--idm-surface)',
                        position: 'sticky',
                        top: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        paddingTop: '12px',
                        cursor: 'pointer',
                    }}
                    onClick={toggleRightBar}
                >
                    <Tooltip title="Show insights panel" placement="left">
                        <Button size="small" type="text" icon={<DoubleLeftOutlined />} />
                    </Tooltip>
                </div>
            ) : window.innerWidth > 1000 ? (
            <div className="desktop" style={{
                width: '320px',
                minWidth: '320px',
                height: '100vh',
                borderLeft: '1px solid var(--idm-border)',
                background: 'var(--idm-surface)',
                display: 'flex',
                flexDirection: 'column',
                position: 'sticky',
                top: 0,
                overflow: 'hidden',
            }}>
                {/* Scrollable area — footer below stays fixed */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px', minHeight: 0 }}>
                {/* Collapse toggle */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '-6px -6px 2px 0' }}>
                    <Tooltip title="Hide panel" placement="left">
                        <Button size="small" type="text" icon={<DoubleRightOutlined />} onClick={toggleRightBar} />
                    </Tooltip>
                </div>
                {/* Section 1: compact features + tutorials */}
                {renderCompactExtras()}
                <div style={{ borderTop: '1px solid var(--idm-border)', margin: '12px 0 16px' }} />

                {/* Section 2: DM Insights */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', position: 'sticky', top: 0, background: 'var(--idm-surface)', zIndex: 1, paddingBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '16px' }}>✨</span>
                        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>DM Insights</h3>
                        {dmInsights?.counts?.unread > 0 && <Badge count={dmInsights.counts.unread} size="small" />}
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <Button size="small" type="text" icon={<ReloadOutlined />} loading={dmInsightsLoading} onClick={() => {
                            callAPI({ url: 'dm-insights', method: 'GET', loadingState: setDmInsightsLoading, dataState: (res) => {
                                if (res?.code === 200) setDmInsights(res);
                            }});
                        }} />
                    </div>
                </div>

                {/* Sort toggle */}
                <Radio.Group size="small" value={dmSortBy} onChange={(e) => setDmSortBy(e.target.value)} style={{ marginBottom: 10, width: '100%' }}>
                    <Radio.Button value="priority" style={{ width: '50%', textAlign: 'center', fontSize: 11 }}>Priority</Radio.Button>
                    <Radio.Button value="latest" style={{ width: '50%', textAlign: 'center', fontSize: 11 }}>Latest</Radio.Button>
                </Radio.Group>

                {dmInsightsLoading && <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /></div>}

                {/* Tag filter cards */}
                {!dmInsightsLoading && dmInsights?.data?.length > 0 && (() => {
                    const tagCounts = {};
                    dmInsights.data.forEach(item => {
                        const seen = new Set();
                        (item.intent_timeline || []).forEach(t => {
                            if (!seen.has(t.intent)) {
                                tagCounts[t.intent] = (tagCounts[t.intent] || 0) + 1;
                                seen.add(t.intent);
                            }
                        });
                        const ci = item.current_intent || 'other';
                        if (!seen.has(ci)) {
                            tagCounts[ci] = (tagCounts[ci] || 0) + 1;
                        }
                    });
                    const sorted = Object.entries(tagCounts).sort((a, b) => b[1] - a[1]);
                    const tagColors = { purchase: '#52c41a', collaboration: '#1677ff', complaint: '#ff4d4f', support: '#ff4d4f', enquiry: '#faad14', partnership: '#722ed1', feedback: '#13c2c2', fan_message: '#eb2f96', brand_deal: '#1677ff', pricing: '#faad14', booking: '#722ed1' };

                    return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                            <div
                                onClick={() => setDmFilterTag(null)}
                                style={{
                                    padding: '4px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer',
                                    background: !dmFilterTag ? 'var(--idm-brand-500)' : '#f5f5f5',
                                    color: !dmFilterTag ? 'white' : '#666',
                                    fontWeight: 600,
                                }}
                            >All ({dmInsights.data.length})</div>
                            {sorted.map(([tag, count]) => (
                                <div
                                    key={tag}
                                    onClick={() => setDmFilterTag(dmFilterTag === tag ? null : tag)}
                                    style={{
                                        padding: '4px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer',
                                        background: dmFilterTag === tag ? (tagColors[tag] || '#666') : '#f5f5f5',
                                        color: dmFilterTag === tag ? 'white' : '#666',
                                        fontWeight: 500, transition: 'all 0.15s',
                                    }}
                                >{tag} ({count})</div>
                            ))}
                        </div>
                    );
                })()}

                {!dmInsightsLoading && !dmInsights?.data?.length && (
                    <div style={{ textAlign: 'center', padding: '40px 10px', color: '#666' }}>
                        <RobotOutlined style={{ fontSize: '32px', color: '#ccc', marginBottom: '12px' }} />
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>No insights yet</p>
                        <p style={{ margin: '8px 0 16px', fontSize: '12px', color: '#aaa', lineHeight: '1.5' }}>
                            AI scans your incoming DMs and tags them as purchase, collaboration, complaint, etc. so you can prioritize what matters.
                        </p>
                        <Button size="small" type="primary" ghost onClick={() => {
                            callAPI({ url: 'dm-insights', method: 'GET', loadingState: setDmInsightsLoading, dataState: (res) => {
                                if (res?.code === 200) setDmInsights(res);
                            }});
                        }}>Refresh</Button>
                    </div>
                )}

                {dmInsights?.data?.filter(item => !dmFilterTag || item.current_intent === dmFilterTag || (item.intent_timeline || []).some(t => t.intent === dmFilterTag))
                .sort((a, b) => dmSortBy === 'latest' ? new Date(b.last_seen || 0) - new Date(a.last_seen || 0) : 0)
                .map((item) => {
                    const priorityColor = item.priority === 'high' ? '#ff4d4f' : item.priority === 'medium' ? '#faad14' : '#52c41a';
                    const timeAgo = (() => {
                        const diff = Date.now() - new Date(item.last_seen).getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 60) return `${mins}m`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h`;
                        return `${Math.floor(hrs / 24)}d`;
                    })();

                    return (
                        <div
                            key={item.sender_id}
                            onClick={() => {
                                if (!item.read) {
                                    callAPI({ url: 'dm-insights', method: 'PUT', body: { sender_id: item.sender_id }, loadingState: () => {}, dataState: () => {} });
                                    setDmInsights(prev => ({
                                        ...prev,
                                        data: prev.data.map(d => d.sender_id === item.sender_id ? { ...d, read: true } : d),
                                        counts: { ...prev.counts, unread: Math.max(0, (prev.counts?.unread || 0) - 1) }
                                    }));
                                }
                                if (item.sender_username) window.open(`https://instagram.com/${item.sender_username}`, '_blank');
                            }}
                            style={{
                                display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px',
                                marginBottom: '4px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s',
                                background: item.read ? 'transparent' : '#f6f8ff',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = item.read ? 'transparent' : '#f6f8ff'; }}
                        >
                            <div style={{ position: 'relative', flexShrink: 0 }}>
                                <Avatar size={36} src={item.sender_detail?.profile_pic}>
                                    {(item.sender_username || '?')[0]?.toUpperCase()}
                                </Avatar>
                                <div style={{ position: 'absolute', bottom: -1, right: -1, width: 10, height: 10, borderRadius: '50%', background: priorityColor, border: '2px solid white' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: item.read ? 500 : 700, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {item.sender_username ? `@${item.sender_username}` : item.sender_id?.slice(0, 10)}
                                    </span>
                                    <span style={{ fontSize: '11px', color: '#aaa', flexShrink: 0 }}>{timeAgo}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                    <Tag color="blue" style={{ margin: 0, fontSize: '10px', padding: '0 4px', lineHeight: '18px' }}>{item.current_intent}</Tag>
                                    {item.sender_detail?.follower_count && (
                                        <span style={{ fontSize: '10px', color: '#aaa' }}>{Number(item.sender_detail.follower_count).toLocaleString()}</span>
                                    )}
                                </div>
                                <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {item.intent_timeline?.[item.intent_timeline.length - 1]?.text || ''}
                                </p>
                            </div>
                            <Button size="small" type="text" danger style={{ flexShrink: 0, opacity: 0.4, fontSize: 11, padding: '0 4px', height: 20 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    callAPI({ url: 'dm-insights', method: 'DELETE', body: { sender_id: item.sender_id }, loadingState: () => {}, dataState: () => {} });
                                    setDmInsights(prev => ({
                                        ...prev,
                                        data: prev.data.filter(d => d.sender_id !== item.sender_id),
                                        counts: { ...prev.counts, total: Math.max(0, (prev.counts?.total || 0) - 1) }
                                    }));
                                }}
                            >✕</Button>
                        </div>
                    );
                })}

                </div>

                {/* View All + Refresh — fixed footer, outside the scroll area */}
                <div style={{ flexShrink: 0, background: 'var(--idm-surface)', padding: '8px 16px', borderTop: '1px solid var(--idm-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button type="link" size="small" onClick={() => navigate('/ig?tab=3')}>View All →</Button>
                    <Button size="small" type="text" icon={<ClockCircleOutlined />} onClick={() => {
                        callAPI({ url: 'dm-insights', method: 'GET', loadingState: setDmInsightsLoading, dataState: (res) => {
                            if (res?.code === 200) setDmInsights(res);
                        }});
                    }}>Refresh</Button>
                </div>
            </div>
            ) : null}
        </div>
    );
}

export default WelcomeOnboardingPage;
